import { BrandLogo } from "@/components/shared/BrandLogo";

import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { LayoutDashboard, CalendarClock, Users, Briefcase, Building2, GitPullRequest, FileSignature, ScrollText, LogOut, KeyRound, Mail, MailCheck, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/inquiries", label: "Booking Inquiries", icon: CalendarClock },
  { to: "/admin/applications", label: "Applications", icon: GitPullRequest },
  { to: "/admin/candidates", label: "Candidates", icon: Users },
  { to: "/admin/users", label: "Users & Credentials", icon: KeyRound },
  { to: "/admin/jobs", label: "Job Posts", icon: Briefcase },
  { to: "/admin/job-catalog", label: "Job Catalog", icon: BookOpen },
  { to: "/admin/properties", label: "Properties", icon: Building2 },
  { to: "/admin/offer-templates", label: "Offer Templates", icon: FileSignature },
  { to: "/admin/email-templates", label: "Email Templates", icon: Mail },
  { to: "/admin/email-logs", label: "Email Log", icon: MailCheck },
  { to: "/admin/audit", label: "Audit Logs", icon: ScrollText },
];

export function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="flex min-h-screen bg-[#0A0D14] text-[#F1F5F9]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 hidden h-screen w-64 flex-col border-r border-white/5 bg-[#121722] lg:flex">
        <div className="shrink-0 border-b border-white/5 px-6 py-5">
          <BrandLogo subtitle="Admin CRM" size="h-10 w-10" text="text-xl" />
        </div>
        <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-3" data-testid="admin-sidebar-nav">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 font-sans text-sm transition-all ${
                  isActive ? "bg-gold/15 text-gold" : "text-[#94A3B8] hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <n.icon size={17} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="shrink-0 border-t border-white/5 bg-[#121722] p-4">
          <p className="truncate px-3 font-sans text-xs text-[#94A3B8]">{user?.email}</p>
          <button onClick={doLogout} data-testid="admin-logout" className="mt-2 flex w-full items-center gap-2 rounded-md border border-white/10 px-3 py-2 font-sans text-sm text-[#F1F5F9] hover:border-gold hover:text-gold">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="fixed left-0 right-0 top-0 z-40 border-b border-white/5 bg-[#121722] lg:hidden">
        <div className="flex items-center justify-between px-5 py-3">
          <BrandLogo subtitle="Admin CRM" size="h-8 w-8" text="text-lg" />
          <button onClick={doLogout} data-testid="admin-logout-mobile" className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 font-sans text-xs text-[#F1F5F9]"><LogOut size={14} /> Sign out</button>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-2" data-testid="admin-mobile-nav">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `whitespace-nowrap rounded-full px-3 py-1.5 font-sans text-xs ${isActive ? "bg-gold/15 text-gold" : "text-[#94A3B8]"}`}>{n.label}</NavLink>
          ))}
        </nav>
      </div>

      <main className="w-full pt-28 lg:ml-64 lg:pt-0">
        <div className="mx-auto max-w-6xl px-6 py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
